import { useNavigate } from 'react-router-dom'
import { Input } from '@/app/common/components/input/Input.tsx'
import { AuthLayout } from '../../layout.tsx'
import { AppRoutePaths } from '../../../../../routing/route_paths.ts'
import { Button } from '@/app/common/components/button/Button.tsx'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { useState } from 'react'
import { SendResetPasswordEmail } from '@/apis/users/requests.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'

export default function EnterEmailPage() {
  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-5">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Forgot Password?
          </h3>
          <span className="text-sm text-gray-500 font-medium">
            Enter your email to reset your password
          </span>
        </div>
        <EnterEmailForm />
      </AuthLayout.Content>
    </AuthLayout>
  )
}

const EnterEmailForm = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const { fireAlert } = useAppAlert()

  const EnterEmailFormSchema = z.object({
    email: z.string().email({ message: 'Please enter a valid email address' }),
  })
  type EnterEmailFormT = z.infer<typeof EnterEmailFormSchema>
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EnterEmailFormT>({
    resolver: zodResolver(EnterEmailFormSchema),
  })

  const onSubmit = async (EmailForm: EnterEmailFormT) => {
    setLoading(true)
    const { error } = await SendResetPasswordEmail(EmailForm.email)
    setLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      navigate(AppRoutePaths.auth.forgot_password.check_email, {
        state: { email: EmailForm.email },
      })
    }
  }

  return (
    <form noValidate className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Email
        </label>
        <div className="mt-1">
          <Input
            {...register('email')}
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="user@acescholarships.org"
          />
          <FormErrorMessage message={errors.email?.message} />
        </div>
      </div>

      <div className="flex">
        <Button type="submit" className="grow space-x-2" disabled={loading}>
          {loading && <CircleLoader size="sm" />}
          <span>{loading ? 'Processing...' : 'Continue'}</span>
        </Button>
      </div>
    </form>
  )
}
